import React, { useState, useEffect } from 'react';
import { useGoogleCalendarConnect } from '../hooks/useGoogleCalendarConnect';

interface GoogleCalendarConnectHookProps {
  onSuccess: () => void;
  onError: (error: string) => void;
  userUid: string;
}

const GoogleCalendarConnectHook: React.FC<GoogleCalendarConnectHookProps> = ({ 
  onSuccess, 
  onError, 
  userUid 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isReady, setIsReady] = useState(false);
  
  const connectGoogleCalendar = useGoogleCalendarConnect();

  useEffect(() => {
    // Check if Google Identity Services is available (loaded globally)
    const checkGoogleServices = () => {
      if (window.google?.accounts?.oauth2) {
        setIsReady(true);
        return;
      }
      
      // Wait for the global script to load with timeout
      let attempts = 0;
      const maxAttempts = 20; // 2 seconds
      
      const checkInterval = setInterval(() => {
        if (window.google?.accounts?.oauth2) {
          setIsReady(true);
          clearInterval(checkInterval);
        } else {
          attempts++;
          if (attempts >= maxAttempts) {
            onError('Google Identity Services failed to load within timeout');
            clearInterval(checkInterval);
          }
        }
      }, 100);
    };
    
    checkGoogleServices();

    // Listen for Google Calendar integration events
    const handleSuccess = () => {
      setIsLoading(false);
      setIsConnected(true);
      onSuccess();
      console.log('🎉 Google Calendar successfully connected via event');
    };

    const handleError = (event: any) => {
      setIsLoading(false);
      onError(event.detail || 'Failed to connect Google Calendar');
      console.log('❌ Google Calendar connection failed via event');
    };

    window.addEventListener('googleCalendarSuccess', handleSuccess);
    window.addEventListener('googleCalendarError', handleError);

    return () => {
      window.removeEventListener('googleCalendarSuccess', handleSuccess);
      window.removeEventListener('googleCalendarError', handleError);
    };
  }, [onError, onSuccess]);

  const handleConnect = () => {
    if (!isReady) {
      onError('Google services not ready');
      return;
    }

    if (isLoading) {
      return; // Prevent multiple clicks
    }

    setIsLoading(true);
    console.log('🚀 Starting Google Calendar connection...');
    
    try {
      // Use the hook - it handles all the OAuth flow internally
      connectGoogleCalendar(userUid);
      
      // Success/error will be handled by the hook's callback
      // Reset loading state after a brief moment (OAuth popup should appear immediately)
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
      
    } catch (error: any) {
      console.error('❌ Error in handleConnect:', error);
      onError(error.message || 'Failed to start Google Calendar connection');
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    onError('Google Calendar connection skipped - you can set this up later in Settings');
  };

  if (isConnected) {
    return (
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center w-16 h-16 mx-auto bg-green-100 rounded-full">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-800">Google Calendar gekoppeld!</h3>
        <p className="text-sm text-gray-600">Je kunt nu afspraken synchroniseren en dubbelboekingen voorkomen.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-center space-y-2">
        <h3 className="text-lg font-semibold text-gray-800">Koppel je Google Calendar</h3>
        <p className="text-sm text-gray-600">
          Synchroniseer je agenda om dubbelboekingen te voorkomen en makkelijk afspraken in te plannen.
        </p>
      </div>

      <div className="space-y-3">
        <button
          onClick={handleConnect}
          disabled={!isReady || isLoading}
          className={`w-full py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-3 ${
            !isReady || isLoading
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Koppelen...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
              </svg>
              Koppel Google Calendar
            </>
          )}
        </button>

        <button
          onClick={handleSkip}
          disabled={isLoading}
          className="w-full py-2 px-4 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          Later instellen
        </button>
      </div>

      {!isReady && (
        <div className="text-center">
          <p className="text-xs text-gray-500">Google services worden geladen...</p>
        </div>
      )}
    </div>
  );
};

export default GoogleCalendarConnectHook;
